import React, { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import IconStar from "@floji-internal/mui-icons/Star";
import IconStarOutline from "@floji-internal/mui-icons/StarOutline";

import * as flomoji from "@floji-internal/noto-emoji";

import { Strings } from "--root/strings/en";

import { IconButton } from "--components/buttons/iconButton";
import { EmojiSprite } from "--components/emoji";
import FadeTransition, { getFadeTransitionStyles } from "--floponents-web/common/FadeTransition";
import { useLongPress } from "--floponents-web/hooks/useLongPress";
import { PopperSection, PopperSurface } from "--floponents-web/molecules/PopperSurface";
import { MenuItemListEntry } from "--floponents-web/molecules/ListEntries/MenuItemListEntry";

const STAR_BUTTON_TIMEOUT_MS = 500;

export const EmojiWrapper = styled.div`
    border-radius: ${props => props.theme.radius.small};
    flex: 0 0 auto;
    padding: ${props => props.theme.spacing(0.5)};
    position: relative;

    :hover {
        background: ${props => props.theme.palette.ui.highlight};
    }
`;

const EmojiStarButton = styled(IconButton)`
    box-shadow: ${props => props.theme.shadows.low};
    background-color: ${props => props.theme.palette.surface};
    opacity: 0;
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(25%, -25%);

    :hover:not(:disabled) {
        background-color: ${props => props.theme.palette.surfaceContrast};
        z-index: 1;
    }

    ${props => getFadeTransitionStyles(props.theme.transitions.short)};
`;

export interface EmojiButtonProps extends React.HTMLAttributes<HTMLDivElement> {
    emoji: flomoji.tPickerEmoji;
    tone?: flomoji.tSkinTone;
    size?: number;
    isStarred?: boolean;
    onStarred?: () => void;
    onUnstarred?: () => void;
}
const EmojiButton = forwardRef<
    HTMLDivElement, EmojiButtonProps
>((props, ref) => {
    const {
        emoji,
        tone,
        size = 32,
        isStarred,
        onStarred,
        onUnstarred,
        ...buttonProps
    } = props;

    const buttonRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (typeof ref === "function") {
            ref(buttonRef.current);
        } else if (ref) {
            ref.current = buttonRef.current;
        }
    }, [buttonRef, ref])

    const handleStarButtonClick = useCallback((ev: React.MouseEvent) => {
        ev.preventDefault();
        ev.stopPropagation();

        setIsTouchMenuOpen(false);

        if (isStarred) {
            onUnstarred?.();
        } else {
            onStarred?.();
        }
    }, [onStarred, onUnstarred, isStarred]);

    // Whether mouse is currently within element
    const [isHovered, setIsHovered] = useState(false);
    const hoverTimeoutId = useRef<number>();

    const [isTouchMenuOpen, setIsTouchMenuOpen] = useState(false);
    const longPressProps = useLongPress({
        onLongPress: () => setIsTouchMenuOpen(true),
        allowedPointerTypes: ["touch"]
    });

    const handleMouseEnter = useCallback((ev: React.MouseEvent<HTMLDivElement>) => {
        buttonProps.onMouseEnter?.(ev);
        hoverTimeoutId.current = window.setTimeout(
            () => setIsHovered(true),
            props.isStarred
                // Use shorter timeout on starred emoji
                ? STAR_BUTTON_TIMEOUT_MS / 2
                : STAR_BUTTON_TIMEOUT_MS
        );
    }, [buttonProps, props.isStarred]);

    const handleMouseLeave = useCallback((ev: React.MouseEvent<HTMLDivElement>) => {
        buttonProps.onMouseLeave?.(ev);
        window.clearTimeout(hoverTimeoutId.current);
        setIsHovered(false);
    }, [buttonProps]);

    const sprite = tone
        ? emoji.tones?.[tone]?.sprite ?? emoji.sprite
        : emoji.sprite;

    const starIcon = props.isStarred ? <IconStar /> : <IconStarOutline />;
    const starTitle = props.isStarred
        ? Strings.emojiPicker.unstarEmoji
        : Strings.emojiPicker.starEmoji;

    return (
        <EmojiWrapper
            ref={buttonRef}
            role="button"
            {...buttonProps}
            {...longPressProps}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <FadeTransition in={isHovered}>
                <EmojiStarButton
                    title={starTitle}
                    icon={starIcon}
                    size="tiny"
                    iconSizeOverride={20}
                    onClick={handleStarButtonClick}

                />
            </FadeTransition>

            {isTouchMenuOpen && (
                <PopperSurface
                    placement="bottom"
                    open={isTouchMenuOpen}
                    triggerRef={buttonRef}
                    onClose={() => setIsTouchMenuOpen(false)}
                    menu
                >
                    <PopperSection menu>
                        <MenuItemListEntry
                            startIcon={starIcon}
                            primary={starTitle}
                            onClick={handleStarButtonClick}
                        />
                    </PopperSection>
                </PopperSurface>
            )}

            <EmojiSprite emoji={sprite} size={size} spriteSize={64} />
        </EmojiWrapper>
    );
});
EmojiButton.displayName = EmojiButton.name;

export default EmojiButton;
